import { Button, Empty, Input, InputRef, Popover, Spin, Tooltip, Typography } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { Dayjs } from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useDebouncedState } from '../../hooks/useDebouncedState'
import useScreenSize from '../../hooks/useScreenSize'
import {
  buildResultSubtitle,
  formatOccurrence,
  formatOccurrenceCount,
  groupResultsByJob,
  hiddenOccurrenceCount,
  isCollapsibleWidth,
  isSearchable,
  occurrenceDate,
  SEARCH_DEBOUNCE_MS,
  shouldCollapseOnBlur,
  shouldShowSearchInput,
  visibleOccurrences
} from './calendarSearchModel'
import { useEventDetailQuery, useScheduledEventSearchQuery } from './scheduleQueries'
import { CalendarEventSearchResult } from './scheduleApi'
import { DateRange } from './scheduleModel'
import JobDrawer from './JobDrawer/JobDrawer'
import { useCalendarSearchHighlight } from './CalendarSearchHighlightContext'
import './CalendarSearch.css'

type Props = {
  onSelectDate: (date: Dayjs) => void
  selectedDate: Dayjs
  visibleDateRange: DateRange
}

const CalendarSearch = ({ onSelectDate, selectedDate, visibleDateRange }: Props) => {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [openedEventId, setOpenedEventId] = useState<number | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])
  const [isExpanded, setIsExpanded] = useState(false)
  const inputRef = useRef<InputRef>(null)
  const { screenSize } = useScreenSize()
  const { state: searchTerm, debouncedState: debouncedSearchTerm, setState: setSearchTerm } = useDebouncedState('', SEARCH_DEBOUNCE_MS)

  const showInput = shouldShowSearchInput(screenSize.width, isExpanded)

  useEffect(() => {
    if (!isCollapsibleWidth(screenSize.width)) {
      setIsExpanded(false)
    }
  }, [screenSize.width])

  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus()
    }
  }, [isExpanded])

  const { setSearchTerm: setHighlightTerm } = useCalendarSearchHighlight()

  useEffect(() => {
    setHighlightTerm(debouncedSearchTerm)
    return () => setHighlightTerm('')
  }, [debouncedSearchTerm, setHighlightTerm])

  const searchQuery = useScheduledEventSearchQuery(debouncedSearchTerm, page)
  const eventDetailQuery = useEventDetailQuery(openedEventId, () => setOpenedEventId(null))

  const onSearchTermChange = (value: string) => {
    setSearchTerm(value)
    setPage(1)
    setExpandedGroups([])
    setOpen(value.trim().length > 0)
  }

  const onOccurrenceClick = (occurrence: CalendarEventSearchResult) => {
    onSelectDate(occurrenceDate(occurrence).startOf('day'))
    setOpenedEventId(occurrence.id)
    setOpen(false)
  }

  const groups = groupResultsByJob(searchQuery.data?.results ?? [])
  const termIsSearchable = isSearchable(debouncedSearchTerm)
  const showSpinner = termIsSearchable && searchQuery.isLoading

  const renderBody = () => {
    if (!termIsSearchable) {
      return <div className="calendar-search__message">Keep typing to search jobs by name, number, or customer.</div>
    }

    if (showSpinner) {
      return <div className="calendar-search__message"><Spin size="small" /></div>
    }

    if (searchQuery.isError) {
      return <div className="calendar-search__message">Something went wrong searching the schedule.</div>
    }

    if (groups.length === 0) {
      return (
        <div className="calendar-search__message">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No scheduled jobs match "${debouncedSearchTerm.trim()}"`}
          />
        </div>
      )
    }

    return (
      <>
        <div className="calendar-search__results">
          {groups.map(group => (
            <div key={group.key} className="calendar-search__group">
              <div className="calendar-search__job-name">{group.jobName}</div>
              {buildResultSubtitle(group) && (
                <Typography.Text type="secondary" className="calendar-search__subtitle">
                  {buildResultSubtitle(group)}
                </Typography.Text>
              )}
              <Typography.Text type="secondary" className="calendar-search__count">
                {formatOccurrenceCount(group.occurrences.length)}
              </Typography.Text>
              {visibleOccurrences(group, expandedGroups.includes(group.key)).map(occurrence => (
                <div
                  key={occurrence.id}
                  className="calendar-search__occurrence"
                  role="button"
                  tabIndex={0}
                  onClick={() => onOccurrenceClick(occurrence)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onOccurrenceClick(occurrence)
                    }
                  }}
                >
                  {formatOccurrence(occurrence)}
                </div>
              ))}
              {hiddenOccurrenceCount(group) > 0 && !expandedGroups.includes(group.key) && (
                <Button
                  type="link"
                  size="small"
                  className="calendar-search__show-more"
                  onClick={() => setExpandedGroups([...expandedGroups, group.key])}
                >
                  {`Show ${hiddenOccurrenceCount(group)} more`}
                </Button>
              )}
            </div>
          ))}
        </div>
        {(page > 1 || searchQuery.data?.hasNextPage) && (
          <div className="calendar-search__paging">
            <Button
              size="small"
              disabled={page === 1 || searchQuery.isFetching}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Typography.Text type="secondary">Page {page}</Typography.Text>
            <Button
              size="small"
              disabled={!searchQuery.data?.hasNextPage || searchQuery.isFetching}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {showInput ? (
        <Popover
          open={open}
          onOpenChange={setOpen}
          trigger="click"
          placement="bottomLeft"
          arrow={false}
          content={<div className="calendar-search__panel">{renderBody()}</div>}
        >
          <Input
            allowClear
            ref={inputRef}
            value={searchTerm}
            prefix={<SearchOutlined />}
            placeholder="Search jobs"
            aria-label="Search scheduled jobs"
            className="calendar-search__input"
            onChange={(e) => onSearchTermChange(e.target.value)}
            onFocus={() => {
              if (searchTerm.trim().length > 0) {
                setOpen(true)
              }
            }}
            onBlur={() => {
              if (shouldCollapseOnBlur(screenSize.width, open, searchTerm)) {
                setIsExpanded(false)
              }
            }}
          />
        </Popover>
      ) : (
        <Tooltip title="Search jobs">
          <Button
            icon={<SearchOutlined />}
            aria-label="Open job search"
            className="calendar-search__toggle"
            onClick={() => setIsExpanded(true)}
          />
        </Tooltip>
      )}

      <JobDrawer
        open={openedEventId !== null}
        onClose={() => setOpenedEventId(null)}
        eventData={eventDetailQuery.data ?? null}
        date={selectedDate}
        dateRange={visibleDateRange}
      />
    </>
  )
}

export default CalendarSearch
